import { useState } from 'react';
import { useEffect } from 'react';
import useZipPhotos from './hooks/useZipPhotos';
import moment from 'moment-timezone';
import CopyToClipboard from './CopyToClipboard';
import { Badge } from '@/src/shadComponents/ui/badge';
import { Loader2 } from 'lucide-react';

export default function DownloadRequestList({
    activationId,
}: {
    activationId: number;
}) {
    const { getZipRequestsForActivation } = useZipPhotos();
    const [zipRequests, setZipRequests] = useState<any[]>([]);

    useEffect(() => {
        const load = async () => {
            const zipRequests = await getZipRequestsForActivation(activationId);
            setZipRequests(zipRequests.data);
        };

        load();

        const interval = setInterval(() => {
            load();
        }, 10000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    if (zipRequests.length === 0) {
        return null;
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 pb-10">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold text-foreground">
                        Download Requests
                    </h1>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        A list of all the download requests for this activation.
                    </p>
                </div>
                <div className="grow" />
                <div className="inline-title"></div>
            </div>
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-border">
                            <thead>
                                <tr>
                                    <th
                                        scope="col"
                                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-foreground sm:pl-3 border-border bg-muted/50"
                                    >
                                        Destination
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-foreground border-border bg-muted/50"
                                    >
                                        Status
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-foreground border-border bg-muted/50"
                                    >
                                        Created At
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-foreground border-border bg-muted/50"
                                    >
                                        Completed At
                                    </th>

                                    <th
                                        scope="col"
                                        className="px-3 py-3.5 text-left text-sm font-semibold text-foreground border-border bg-muted/50"
                                    >
                                        Zip Link
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-background">
                                {zipRequests.map((zipRequest) => (
                                    <tr
                                        key={zipRequest.id}
                                        className="even:bg-muted/50"
                                    >
                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-foreground sm:pl-3">
                                            {zipRequest.zipRequestDestination}
                                        </td>
                                        <td className="whitespace-nowrap capitalize px-3 py-4 text-sm text-muted-foreground flex items-center">
                                            {zipRequest.status}
                                            {zipRequest.status ===
                                                'pending' && (
                                                <Loader2 className="ml-2 inline h-4 w-4 animate-spin text-muted-foreground" />
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">
                                            {moment(
                                                zipRequest.createdAt,
                                            ).format('MM/DD/YYYY hh:mm:ss a')}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-muted-foreground">
                                            {zipRequest.completedAt
                                                ? moment(
                                                      zipRequest.completedAt,
                                                  ).format(
                                                      'MM/DD/YYYY hh:mm:ss a',
                                                  )
                                                : ' - '}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-foreground flex items-center">
                                            {zipRequest.zipRequestUri ? (
                                                <>
                                                    <a
                                                        href={
                                                            zipRequest.zipRequestUri
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline"
                                                    >
                                                        Download
                                                    </a>
                                                    <CopyToClipboard
                                                        text={
                                                            zipRequest.zipRequestUri
                                                        }
                                                    />
                                                </>
                                            ) : (
                                                ' - '
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
